// Tool-Logo-Registry. Statische ESM-Imports, damit Vite die Assets bündelt.
// Lookup ist tolerant: lowercase, alles was kein a-z0-9 ist wird ignoriert.

import wordpress from "@/assets/logos/wordpress.svg";
import woocommerce from "@/assets/logos/woocommerce.svg";
import shopware from "@/assets/logos/shopware.svg";
import shopify from "@/assets/logos/shopify.svg";
import typo3 from "@/assets/logos/typo3.svg";
import strapi from "@/assets/logos/strapi.svg";
import storyblok from "@/assets/logos/storyblok.svg";
import googleAds from "@/assets/logos/google-ads.svg";
import googleAnalytics from "@/assets/logos/google-analytics.svg";
import googleTagManager from "@/assets/logos/google-tag-manager.svg";
import googleSearchConsole from "@/assets/logos/google-search-console.webp";
import lookerStudio from "@/assets/logos/looker-studio.svg";
import bing from "@/assets/logos/bing.svg";
import meta from "@/assets/logos/meta.png";
import tiktok from "@/assets/logos/tiktok.svg";
import linkedin from "@/assets/logos/linkedin.svg";
import screamingFrog from "@/assets/logos/screaming-frog.jpg";
import ahrefs from "@/assets/logos/ahrefs.webp";
import sistrix from "@/assets/logos/sistrix.png";
import matomo from "@/assets/logos/matomo.png";
import seobility from "@/assets/logos/seobility.webp";
import delta from "@/assets/logos/delta.svg";
import hotjar from "@/assets/logos/hotjar.svg";
import microsoftClarity from "@/assets/logos/microsoft-clarity.svg";
import pageSpeedInsights from "@/assets/logos/pagespeed-insights.svg";
import lighthouse from "@/assets/logos/lighthouse.svg";
import consentManagement from "@/assets/logos/consent-management.svg";
import wetracked from "@/assets/logos/wetracked.svg";
import bigquery from "@/assets/logos/bigquery.svg";
import platformApis from "@/assets/logos/platform-apis.svg";

// key → asset url
const REGISTRY: Record<string, string> = {
  wordpress,
  woocommerce,
  shopware,
  shopify,
  typo3,
  strapi,
  storyblok,
  googleads: googleAds,
  googleanalytics: googleAnalytics,
  googleanalyticsga4: googleAnalytics,
  googleanalytics4: googleAnalytics,
  ga4: googleAnalytics,
  googletagmanager: googleTagManager,
  gtm: googleTagManager,
  googlesearchconsole: googleSearchConsole,
  gsc: googleSearchConsole,
  lookerstudio: lookerStudio,
  bing: bing,
  microsoftads: bing,
  metaadsmanager: meta,
  metaads: meta,
  meta,
  tiktokadsmanager: tiktok,
  tiktokads: tiktok,
  tiktok,
  linkedincampaignmanager: linkedin,
  linkedinads: linkedin,
  linkedin,
  screamingfrog: screamingFrog,
  ahrefs,
  sistrix,
  matomo,
  seobility,
  delta,
  hotjar,
  microsoftclarity: microsoftClarity,
  clarity: microsoftClarity,
  pagespeedinsights: pageSpeedInsights,
  pagespeed: pageSpeedInsights,
  lighthouse,
  consentmanagementplattformen: consentManagement,
  consentmanagementplatforms: consentManagement,
  consentmanagement: consentManagement,
  wetracked,
  bigquery,
  platformapis: platformApis,
  plattformapis: platformApis,
};

function normalize(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, "");
}

// Logos die bereits eine Wortmarke (Tool-Name als Schrift) enthalten.
// Bei diesen wird in der Card der Title weggelassen, um Doppelung zu vermeiden.
const WORDMARK_LOGOS = new Set<string>([
  "typo3",
  "storyblok",
  "woocommerce",
  "shopware",
  "googleads",
  "googleanalytics",
  "googleanalyticsga4",
  "ga4",
  "googletagmanager",
  "gtm",
  "googlesearchconsole",
  "gsc",
  "lookerstudio",
  "screamingfrog",
  "ahrefs",
  "sistrix",
  "matomo",
  "seobility",
  "linkedin",
  "linkedincampaignmanager",
  "tiktok",
  "tiktokadsmanager",
]);

export function getToolLogo(name: string): string | undefined {
  return REGISTRY[normalize(name)];
}

export function getToolLogoInfo(name: string): { src: string; isWordmark: boolean } | undefined {
  const key = normalize(name);
  const src = REGISTRY[key];
  if (!src) return undefined;
  return { src, isWordmark: WORDMARK_LOGOS.has(key) };
}

export function getToolLogoInfos(name: string): { src: string; isWordmark: boolean }[] {
  const direct = getToolLogoInfo(name);
  if (direct) return [direct];

  const parts = name
    .split(/\s*(?:\/|\+|,|&| und | and )\s*/i)
    .map((part) => part.trim())
    .filter(Boolean);

  const seen = new Set<string>();
  return parts.flatMap((part) => {
    const info = getToolLogoInfo(part);
    if (!info || seen.has(info.src)) return [];
    seen.add(info.src);
    return [info];
  });
}
